import { GoodsShipmentItem } from 'store/declarations/ireland/import-declaration';
import { IrelandImportReleasedNotification } from 'store/declarations/ireland/released-notification';
import { CalculationResult, CalculationSummary } from './types';
import { getTaxPriority } from './utils';

const isVatType = (t?: string | null) => t === 'B00' || t === '1B2';

const round2 = (n: number) => Math.round((n + Number.EPSILON) * 100) / 100;

export const getTaxTypeDescription = (code: string) => {
    const map: { [key: string]: string } = {
        A00: 'Customs duties on industrial products',
        B00: 'VAT',
        '1B2': 'Deferred VAT',
    };
    return map[code] || '';
};

const computeTotals = (rows: CalculationResult[], perRowStatValues?: number[]) => {
    let totalDuty = 0;
    let totalVat = 0;
    let totalLiability = 0;
    let totalStatisticalValue = 0;

    for (let i = 0; i < rows.length; i++) {
        const row = rows[i];
        for (const tax of row.taxes) {
            const amt = Number(tax.assessedAmount ?? 0);
            if (!isVatType(tax.taxType)) totalDuty += amt;
            else if (tax.taxType === 'B00' || tax.taxType === '1B2') totalVat += amt;
        }
        totalLiability += Number(row.totalLiability ?? 0);

        if (perRowStatValues && perRowStatValues[i] != null) {
            totalStatisticalValue += Number(perRowStatValues[i]);
        }
    }

    return {
        totalDuty: round2(totalDuty),
        totalVat: round2(totalVat),
        totalLiability: round2(totalLiability),
        totalStatisticalValue: round2(totalStatisticalValue),
    };
};

export const calculateRevenueData = (notification: IrelandImportReleasedNotification | null): CalculationSummary => {
    const items = notification?.goodsShipment?.goodsShipmentItem || [];

    const rows = items.map((item) => {
        const taxBoxes = item.taxes?.taxBox43Bis || [];

        const taxes = taxBoxes.map((tax) => {
            const taxType = tax.boxTaxType ?? '';
            const taxBase = Number(tax.boxAmount ?? 0);
            const taxRate = Number(tax.boxTaxRate ?? 0);
            const assessedAmount =
                tax.boxTaxPayableAmount != null ? Number(tax.boxTaxPayableAmount) : (taxBase * taxRate) / 100;
            return { taxType, taxBase, taxRate, assessedAmount };
        });

        const totalLiability = taxes.reduce((sum, t) => sum + (t.assessedAmount ?? 0), 0);

        return {
            index: item.goodsItemNumber16,
            details: `Commodity Code: ${
                item.goodsInformation?.commodityCode?.combinedNomenclatureCode614 ?? ''
            }\nCountry of Origin: ${item?.datesPlaces?.countryOrigin515 ?? ''}`,
            taxes,
            totalLiability,
        };
    });

    const perRowSV = items.map((it) => Number((it as any).statisticalValue ?? (it as any).itemAmount ?? 0));

    const totals = computeTotals(rows, perRowSV);
    return { rows, ...totals, numberOfItems: items.length };
};

export const calculateLocalData = (products: GoodsShipmentItem[] | null): CalculationSummary => {
    if (!products) {
        return {
            rows: [],
            totalDuty: 0,
            totalLiability: 0,
            totalVat: 0,
            totalStatisticalValue: 0,
            numberOfItems: 0,
        };
    }

    const perRowSV: number[] = [];

    const rows = products.map((item) => {
        const statisticalValue = Number(item.statisticalValue ?? item.itemAmount ?? 0);
        perRowSV.push(statisticalValue);

        const localTaxes = item?.localTaxes ?? [];

        const taxes = localTaxes.map((tb) => {
            const taxType = tb?.boxTaxType ?? '';
            const taxBase = Number(tb?.boxAmount ?? 0);
            const taxRate = Number(tb?.boxTaxRate ?? 0);
            const assessedAmount = Number(tb?.boxTaxPayableAmount ?? 0);
            return { taxType, taxBase, taxRate, assessedAmount };
        });

        taxes.sort((a, b) => getTaxPriority(a.taxType) - getTaxPriority(b.taxType));

        const totalLiability = round2(taxes.reduce((s, t) => s + Number(t.assessedAmount || 0), 0));

        return {
            index: String(item?.goodsItemNumber),
            details: `Commodity Code: ${item?.goodsInformation?.combinedNomenclatureCode ?? '-'}\nCountry of Origin: ${
                item?.countryOrigin ?? '-'
            }`,
            taxes,
            totalLiability,
        };
    });

    const totals = computeTotals(rows, perRowSV);
    return { rows, ...totals, numberOfItems: products.length };
};
